/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React from 'react';
import { Box, Heading, VStack, HStack, SimpleGrid, Flex, Image, Grid, GridItem, Separator } from 'chakra-v3';
import { Text } from '../components/Text';
import { Link } from '../components/Link';
import Header from '../components/headers/Header';
import { Button } from '../components/buttons/Index';
import { CaseStudyCard } from '../components/CaseStudyCard';
import { FeatureCard } from '../components/FeatureCard';
import { NumberedFeature } from '../components/NumberedFeature';
import { StepCard } from '../components/StepCard';
import AIIcon from '../assets/icons/AIIcon';
import unionPolygons from '../assets/design/union_polygons.svg';
import heroDesign from '../assets/design/hero_design.svg';
import { karyaConfig } from '../config/company';
import { withBaseUrl } from '../utils/url';
import { foundationFeatures, numberedFeatures, steps, caseStudies, LOGOS } from './homepageData';

const Homepage: React.FC = () => {
  const samikshaUrl = import.meta.env.VITE_SAMIKSHA_URL || '/samiksha';
  const isSamikshaExternal = samikshaUrl.startsWith('http://') || samikshaUrl.startsWith('https://');

  // Configuration for hero design positions
  // Positions calculated to create overlapping visual effect based on design specs
  const heroDesignPositions = [
    { side: 'right' as const, top: '-32.85px', zIndex: 3 },
    { side: 'left' as const, top: '-32.85px', zIndex: 3 },
    { side: 'right' as const, top: '279.78px', zIndex: 2 },
    { side: 'left' as const, top: '279.78px', zIndex: 2 },
    { side: 'right' as const, top: '592.41px', zIndex: 1 },
    { side: 'left' as const, top: '592.41px', zIndex: 1 },
  ] as const;

  return (
    <Box minH="100vh" bg="bg.primary" color="text.teal" position="relative" overflowX="hidden">
      {/*
         SECTION 1: HEADER & HERO
         Kept separate with a solid background so they remain clean.
      */}
      <Box position="relative" zIndex={20} bg="bg.primary">
        <Header />

        <Box id="hero" position="relative">
          {/* Hero Design SVG - Positioned to span from just after header to end of hero section */}
          <Box position="absolute" inset={0} overflow="hidden" pointerEvents="none" zIndex={0}>
            {heroDesignPositions.map((config, index) => (
              <Image
                key={`${config.side}-${config.top}-${index}`}
                src={heroDesign}
                alt=""
                position="absolute"
                {...(config.side === 'right' ? { right: '-1px' } : { left: '10px' })}
                top={config.top}
                zIndex={config.zIndex}
                w="327px"
                h="313px"
                display={{ base: 'none', lg: 'block' }}
                pointerEvents="none"
                aria-hidden="true"
              />
            ))}
          </Box>
          <Flex
            minH={{ base: 'calc(100vh - 65px)', md: 'calc(100vh - 95px)' }}
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            py={{ base: 16, md: 20 }}
            px={{ base: 3, sm: 4, md: 6 }}
            position="relative"
            zIndex={1}
          >
            <VStack gap={{ base: 8, md: '48px' }} align="center" maxW="922px" w="100%">
              <VStack gap={{ base: 8, md: '48px' }} align="center" w="100%" textAlign="center">
                <Box position="relative" w="100%" px={{ base: 2, sm: 0 }}>
                  <Heading
                    as="h1"
                    fontSize={{ base: '28px', sm: '32px', md: '68px' }}
                    fontWeight="normal"
                    fontFamily="'Noto Serif', serif"
                    lineHeight={{ base: '1.2', sm: '1.2', md: '79px' }}
                    color="text.hero"
                    letterSpacing={{ base: '-1px', sm: '-1.5px', md: '-2.72px' }}
                    wordBreak="break-word"
                    overflowWrap="break-word"
                  >
                    Evaluations to reflect India at{' '}
                    <Text
                      as="span"
                      variant="inherit"
                      color="text.accent"
                      position="relative"
                      display="inline-block"
                      fontSize="inherit"
                      fontFamily="inherit"
                      fontWeight="inherit"
                      lineHeight="inherit"
                      letterSpacing="inherit"
                    >
                      Scale
                      <Box
                        as="span"
                        position="absolute"
                        right={{ base: '-12px', sm: '-16px', md: '-24px' }}
                        top={{ base: '2px', sm: '2px', md: '4px' }}
                        w={{ base: '12px', sm: '14px', md: '26px' }}
                        h={{ base: '12px', sm: '14px', md: '26px' }}
                        display="inline-block"
                        lineHeight={0}
                      >
                        <AIIcon width="100%" height="100%" />
                      </Box>
                    </Text>
                  </Heading>
                </Box>
                <Text
                  fontSize={{ base: '16px', sm: '18px', md: '20px' }}
                  color="text.accent"
                  fontFamily="'Plus Jakarta Sans', sans-serif"
                  fontWeight="500"
                  lineHeight="1.4"
                  letterSpacing="0.05px"
                  px={{ base: 2, sm: 0 }}
                  w="100%"
                >
                  AI systems are increasingly used in public-facing and high stakes settings. Yet many models still miss
                  the cultural cues, linguistic nuances and everyday realities of millions of Indians. As a result, they
                  fall short of being useful, trustworthy and culturally aligned.
                </Text>
              </VStack>
              <Box
                position="relative"
                borderRadius="4px"
                p="1px"
                w="100%"
                maxW={{ base: '100%', sm: 'auto' }}
                bg="border.darkStroke"
              >
                <Box
                  bg="brand.teal"
                  minH="64px"
                  px={{ base: 3, sm: 4, md: '16px' }}
                  py={{ base: 3, sm: 4, md: '8px' }}
                  borderRadius="3px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap="16px"
                >
                  <Text
                    color="text.primary"
                    textAlign="center"
                    fontSize={{ base: '14px', sm: '16px' }}
                    fontFamily="'DM Sans', sans-serif"
                    fontWeight="normal"
                    lineHeight="1.4"
                    letterSpacing="0.08px"
                  >
                    Karya helps you understand how your models perform across this full spectrum — from urban experts to
                    rural smartphone users.
                  </Text>
                </Box>
              </Box>
              <HStack
                gap={{ base: 3, sm: 6 }}
                pt={4}
                w="100%"
                flexDirection={{ base: 'column', sm: 'row' }}
                justifyContent="center"
                alignItems="stretch"
              >
                <Button variant="secondary" w={{ base: '100%', sm: '180px' }} asChild>
                  <a
                    href={samikshaUrl}
                    target={isSamikshaExternal ? '_blank' : '_self'}
                    rel={isSamikshaExternal ? 'noopener noreferrer' : undefined}
                  >
                    View Samiksha
                  </a>
                </Button>
              </HStack>
            </VStack>
          </Flex>
        </Box>
      </Box>
      {/*
         SECTION 2: THE "Gradient Wrapper" WRAPPER
         This contains all subsequent sections.
         It has the gradient background applied directly to it.
         Inner sections have their backgrounds removed (transparent).
      */}
      <Box
        position="relative"
        // The Gradient: Blue (50% opacity) at top -> Transparent by 40% height
        bgImage="linear-gradient(0deg, #F2EDE7 12.18%, #C7EAF1 63.86%, #90E7FF 81.09%)"
        zIndex={1}
      >
        {/* Recognised By Section - bg removed */}
        <Box id="recognised" py={{ base: 16, md: '80px' }} px={{ base: 4, md: '60px' }}>
          <VStack gap={{ base: 8, md: '60px' }} maxW="1188px" mx="auto" w="100%">
            <VStack gap={{ base: 2, md: '8px' }} textAlign="center">
              <Text
                color="text.teal"
                fontFamily="'Plus Jakarta Sans', sans-serif"
                fontSize="24px"
                fontWeight="500"
                lineHeight="1.4"
              >
                Recognised by leading organisations in
              </Text>
              <Heading
                as="h2"
                fontSize="32px"
                fontWeight="normal"
                color="text.teal"
                fontFamily="'DM Sans', sans-serif"
                lineHeight="1.4"
                letterSpacing="0.32px"
              >
                AI research, development and public policy
              </Heading>
            </VStack>
            <Flex
              wrap="nowrap"
              justify="center"
              align="center"
              gap={{ base: 6, md: 12 }}
              maxW="6xl"
              mx="auto"
              px={4}
              overflowX="auto"
            >
              {LOGOS.map((logo) => (
                <Image
                  key={logo.name}
                  src={logo.src}
                  alt={logo.name}
                  h={{ base: '28px', md: '36px' }}
                  objectFit="contain"
                  w="auto"
                  flexShrink={0}
                />
              ))}
            </Flex>
          </VStack>
        </Box>

        {/* Reliable Foundation Section - bg removed */}
        <Box id="features-section" py={{ base: 16, md: '80px' }} px={{ base: 4, md: '60px' }}>
          <VStack gap={{ base: 12, md: '60px' }} maxW="1188px" mx="auto" alignItems="center" w="100%">
            <VStack gap={{ base: 4, md: '24px' }} textAlign="center" maxW="986px" w="100%">
              <Heading
                as="h2"
                fontSize={{ base: '32px', md: '40px' }}
                fontWeight="normal"
                color="text.teal"
                fontFamily="'Noto Serif', serif"
                lineHeight="normal"
              >
                A reliable foundation for evaluating language models
              </Heading>
              <Text
                color="text.darkTeal"
                fontSize={{ base: 'lg', md: '24px' }}
                fontFamily="'Plus Jakarta Sans', sans-serif"
                fontWeight="500"
                lineHeight="1.4"
                letterSpacing="0.06px"
                maxW={{ base: '100%', md: '1114px' }}
              >
                Karya Eval Platform provides a structured environment for empirical assessment of model quality. Each
                study combines verified participants, clearly defined protocols and transparent governance. The result
                is an evaluation process that aligns with the standards of research-grade reproducibility, while
                operating efficiently at scale.
              </Text>
            </VStack>
            <SimpleGrid columns={{ base: 1, md: 3 }} gap={{ base: 4, md: '24px' }} w="100%" maxW="91.7%">
              {foundationFeatures.map((feature, index) => (
                <FeatureCard key={index} icon={feature.icon} title={feature.title} description={feature.description} />
              ))}
            </SimpleGrid>
            <Button
              variant="primary"
              onClick={() => {
                if (typeof window !== 'undefined') {
                  window.location.href = 'mailto:sales@karya.in';
                }
              }}
              w="177px"
            >
              Learn how it works
            </Button>
          </VStack>
        </Box>

        {/* Built for Precision Section - bg removed */}
        <Box id="precision" py={{ base: 16, md: '80px' }} px={{ base: 4, md: '60px' }}>
          <VStack gap={{ base: 12, md: '60px' }} maxW="1188px" mx="auto" alignItems="center" w="100%">
            <Box position="relative" w="100%">
              <Grid
                templateColumns={{ base: '1fr', lg: 'repeat(3, 1fr)' }}
                templateRows={{ base: 'auto', lg: 'repeat(2, auto)' }}
                columnGap={{ base: 0, lg: '48px' }}
                rowGap={{ base: 8, lg: '48px' }}
                w="100%"
              >
                <GridItem
                  colStart={{ base: 1, lg: 1 }}
                  rowStart={{ base: 1, lg: 1 }}
                  display="flex"
                  alignItems={{ base: 'center', lg: 'flex-start' }}
                  justifyContent={{ base: 'center', lg: 'flex-start' }}
                >
                  <Heading
                    as="h2"
                    fontSize={{ base: '32px', lg: '40px' }}
                    fontWeight="normal"
                    color="text.dark"
                    fontFamily="heading"
                    lineHeight="1.2"
                    textAlign={{ base: 'center', lg: 'left' }}
                  >
                    Built for precision, scale and transparency
                  </Heading>
                </GridItem>

                <Separator
                  display={{ base: 'none', lg: 'block' }}
                  position="absolute"
                  left={0}
                  right={0}
                  top="50%"
                  transform="translateY(-50%)"
                  orientation="horizontal"
                  size="sm"
                  borderColor="text.accent"
                  pointerEvents="none"
                  zIndex={0}
                />

                <Separator
                  display={{ base: 'none', lg: 'block' }}
                  position="absolute"
                  left="calc((100% - 96px) / 3 + 24px)"
                  top={0}
                  bottom={0}
                  orientation="vertical"
                  size="sm"
                  borderColor="text.accent"
                  pointerEvents="none"
                  zIndex={0}
                />

                <Separator
                  display={{ base: 'none', lg: 'block' }}
                  position="absolute"
                  left="calc(2 * (100% - 96px) / 3 + 48px + 24px)"
                  top={0}
                  bottom={0}
                  orientation="vertical"
                  size="sm"
                  borderColor="text.accent"
                  pointerEvents="none"
                  zIndex={0}
                />

                {numberedFeatures.map((feature) => (
                  <NumberedFeature
                    key={feature.number}
                    number={feature.number}
                    title={feature.title}
                    description={feature.description}
                    colStart={feature.colStart}
                    rowStart={feature.rowStart}
                  />
                ))}
              </Grid>
            </Box>
          </VStack>
        </Box>

        {/* Case Studies Section - bg removed */}
        <Box id="case-studies" py={{ base: 16, md: '80px' }} px={{ base: 4, md: '60px' }}>
          <VStack gap={{ base: 12, md: '64px' }} maxW="91.7%" mx="auto" alignItems="center" w="100%">
            <VStack gap={{ base: 4, md: '24px' }} textAlign="center" maxW="986px" w="100%">
              <Heading
                as="h2"
                fontSize={{ base: '32px', md: '40px' }}
                fontWeight="normal"
                color="text.dark"
                fontFamily="'Noto Serif', serif"
                lineHeight="normal"
              >
                Evaluations that inform responsible deployment
              </Heading>
              <Text
                color="text.darkTeal"
                fontSize={{ base: 'lg', md: '24px' }}
                fontFamily="'Plus Jakarta Sans', sans-serif"
                fontWeight="500"
                lineHeight="1.4"
                letterSpacing="0.06px"
                maxW={{ base: '100%', md: '1114px' }}
              >
                Karya Eval Platform underpins studies where accuracy, contextual sensitivity and ethical reliability are
                essential to safe AI adoption
              </Text>
            </VStack>

            <Flex
              direction={{ base: 'column', lg: 'row' }}
              gap="64px"
              alignItems="stretch"
              justifyContent="center"
              w="100%"
              maxW="1200px"
            >
              {caseStudies.map((caseStudy, index) => (
                <CaseStudyCard
                  key={index}
                  title={caseStudy.title}
                  description={caseStudy.description}
                  href={caseStudy.href}
                />
              ))}
            </Flex>

            <Button variant="primary" w="194px" asChild>
              <a
                href={samikshaUrl}
                target={isSamikshaExternal ? '_blank' : '_self'}
                rel={isSamikshaExternal ? 'noopener noreferrer' : undefined}
              >
                View Samiksha
              </a>
            </Button>
          </VStack>
        </Box>

        {/* From Protocol to Insight Section - bg removed */}
        <Box id="protocol" py={{ base: 16, md: '80px' }} px={{ base: 4, md: '60px' }}>
          <VStack gap={{ base: 12, md: '56px' }} maxW="91.7%" mx="auto" alignItems="center" w="100%">
            <Heading
              as="h2"
              fontSize={{ base: '32px', md: '40px' }}
              fontWeight="normal"
              color="text.teal"
              fontFamily="'Noto Serif', serif"
              lineHeight="normal"
              textAlign="center"
            >
              From protocol to insight
            </Heading>

            <Box position="relative" w="100%" maxW="1230px" py={{ base: 8, md: 0 }}>
              <Separator
                display={{ base: 'none', md: 'block' }}
                position="absolute"
                top="30px"
                left={{ md: 'calc(16.67% - 5px)', lg: 'calc(16.67% - 20px)' }}
                right={{ md: 'calc(16.67% - 5px)', lg: 'calc(16.67% - 20px)' }}
                orientation="horizontal"
                colorPalette="brand.grey"
                size="sm"
              />

              <SimpleGrid
                columns={{ base: 1, md: 3 }}
                gap={{ base: 8, md: '32px', lg: '80px' }}
                position="relative"
                zIndex={1}
                justifyContent="center"
                alignItems="flex-start"
                w="100%"
              >
                {steps.map((step) => (
                  <StepCard key={step.number} number={step.number} title={step.title} description={step.description} />
                ))}
              </SimpleGrid>
            </Box>

            <Button
              variant="primary"
              w="195px"
              onClick={() => {
                if (typeof window !== 'undefined') {
                  window.location.href = 'mailto:sales@karya.in';
                }
              }}
            >
              Start your evaluation
            </Button>
          </VStack>
        </Box>

        {/* Grounded in Fairness Section - bg removed */}
        <Box
          id="fairness"
          pt={{ base: 28, md: '140px' }}
          pb={{ base: 16, md: '80px' }}
          px={{ base: 4, md: '60px' }}
          position="relative"
          overflow="hidden"
        >
          <Image
            src={unionPolygons}
            alt=""
            display={{ base: 'none', md: 'block' }}
            position="absolute"
            top={{ md: '2%' }}
            left="50%"
            transform="translateX(-50%)"
            w={{ md: '170%', lg: '105%' }}
            maxW="1280px"
            opacity={0.45}
            pointerEvents="none"
            aria-hidden="true"
          />
          <VStack
            gap={{ base: 8, md: '45px' }}
            maxW="91.7%"
            mx="auto"
            alignItems="center"
            justifyContent="center"
            position="relative"
            zIndex={1}
          >
            <VStack gap={{ base: 4, md: '24px' }} alignItems="center" justifyContent="center">
              <Heading
                as="h2"
                fontSize={{ base: '40px', md: '68px' }}
                fontWeight="normal"
                color="text.teal"
                fontFamily="'Noto Serif', serif"
                lineHeight={{ base: '1.2', md: '79px' }}
                letterSpacing="-2.72px"
                textAlign="center"
                maxW={{ base: '100%', lg: '1200px' }}
              >
                Grounded in fairness and accountability
              </Heading>
              <Text
                color="text.darkTeal"
                fontSize={{ base: 'lg', md: '24px' }}
                fontFamily="'Plus Jakarta Sans', sans-serif"
                fontWeight="500"
                lineHeight="1.4"
                letterSpacing="0.06px"
                textAlign="center"
                maxW={{ base: '100%', md: '1114px' }}
              >
                Every evaluation on the platform contributes to dignified, fairly paid work for verified contributors.
                Partnering with Karya ensures that each study not only advances model quality but also strengthens the
                ethical foundations of AI research.
              </Text>
            </VStack>
            <VStack
              gap={{ base: 8, md: '40px' }}
              alignItems="center"
              justifyContent="center"
              maxW="986px"
              w="100%"
              pt={{ base: 12, md: '80px' }}
            >
              <Text
                color="text.teal"
                fontSize={{ base: '28px', md: '40px' }}
                fontFamily="'Plus Jakarta Sans', sans-serif"
                fontWeight="500"
                lineHeight="normal"
                textAlign="center"
                maxW={{ base: '100%', md: '832px' }}
                px={4}
              >
                Integrity in evaluation is as important as performance in modelling.
                <br />
                <br />
                Karya ensures both.
              </Text>
              <Button
                variant="primary"
                w="279px"
                onClick={() => {
                  if (typeof window !== 'undefined') {
                    window.open('https://impact.karya.in', '_blank', 'noopener,noreferrer');
                  }
                }}
              >
                Learn more about our approach
              </Button>
            </VStack>
          </VStack>
        </Box>

        {/* Footer - Keep bg.primary to distinguish it from the content flow */}
        <Box bg="bg.primary" py={{ base: 12, md: '100px' }} px={{ base: 6, md: '60px' }}>
          <Flex
            direction={{ base: 'column', lg: 'row' }}
            justify="space-between"
            align={{ base: 'flex-start', lg: 'flex-start' }}
            gap={{ base: 12, lg: 8 }}
            maxW="91.7%"
            mx="auto"
            w="100%"
          >
            {/* Left Column - Logo and Tagline */}
            <VStack align="flex-start" gap={6} maxW={{ base: '100%', lg: '320px' }}>
              <Image src={withBaseUrl('/karya_logo_with_text.svg')} alt="Karya Evals Logo" />
              <Text
                color="text.primary"
                fontSize="24px"
                fontFamily="'Noto Serif', serif"
                fontWeight="normal"
                lineHeight="normal"
              >
                Create meaningful impact with every evaluation.
              </Text>
            </VStack>

            {/* Middle Column - Navigation Links */}
            <VStack align="flex-start" gap="19px" display={{ base: 'flex', lg: 'flex' }}>
              <Link href="#features-section" variant="footer">
                <Text variant="link">Features</Text>
              </Link>
              <Text variant="link" color="text.primary" cursor="pointer" _hover={{ color: 'text.accent' }} asChild>
                <a
                  href={samikshaUrl}
                  target={isSamikshaExternal ? '_blank' : '_self'}
                  rel={isSamikshaExternal ? 'noopener noreferrer' : undefined}
                >
                  Case Studies
                </a>
              </Text>
              <Link href="mailto:sales@karya.in?subject=Enquiry regarding custom data needs" variant="footer">
                <Text variant="link">Custom Data Needs</Text>
              </Link>
              <Link href="https://karya.in" target="_blank" rel="noopener noreferrer" variant="footer">
                <Text variant="link">Learn more</Text>
              </Link>
            </VStack>

            {/* Right Column - Contact Info and Address */}
            <VStack align="flex-start" gap={6} maxW={{ base: '100%' }}>
              <VStack align="flex-start" gap={0}>
                <Text variant="caption" color="text.accent">
                  Contact
                </Text>
                <Text variant="caption" color="text.accent">
                  {karyaConfig.email}
                </Text>
              </VStack>
              <VStack align="flex-start" gap={0}>
                <Text variant="caption" color="text.primary">
                  © {new Date().getFullYear()} {karyaConfig.name}
                </Text>
                {karyaConfig.officeAddress.split('\n').map((addressLine, index) => (
                  <Text key={index} variant="caption" color="text.primary">
                    {addressLine}
                  </Text>
                ))}
              </VStack>
            </VStack>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};

export default Homepage;
